import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    ArrowLeft, Crown, Globe, RefreshCw, CheckCircle2, XCircle, Hash, CreditCard, Ban,
    Receipt, IndianRupee, CalendarCheck
} from 'lucide-react';
import StatusChip from '../../../../../components/statusChip/StatusChip';
import CommonButton from '../../../../../components/commonButton';
import { TabCard } from '../../../../../components/custom-card/Cards';
import { formatMomentDate } from '../../../../../utils/dateHelper';
import { Card } from 'react-bootstrap';
import CustomTable from '../../../../../components/table/CustomTable';
import { fetchPaymentTransactions, downloadPaymentInvoice } from '../../../../../redux/slice/subscriptionSlice';
import { useToast } from '../../../../../context/TostContext';
import { saveAs } from 'file-saver';
import { fetchUserSubscriptions, reverseUserSubscription, fetchUserDetail } from '../../../../../redux/slice/allUsersSlice';
import ConfirmModal from '../../../../../components/withModal/ConfirmModal';

const getStatusBg = (status) => {
    const lowerStatus = status?.toLowerCase();
    switch (lowerStatus) {
        case "active":
            return "chip-green";
        case "trialing":
        case "initialized":
            return "chip-blue";
        case "cancelled":
            return "chip-warning";
        case "expired":
            return "chip-secondary";
        case "paid":
            return "chip-green";
        case "revoked":
        case "failed":
            return "chip-red";
        case "pending":
            return "chip-warning";
        default:
            return "custom-status-chip";
    }
};

const DetailRow = ({ label, value }) => (
    <div className="d-flex flex-column mb-3">
        <span className="text-muted fs-sm">{label}</span>
        <p className="fw-medium fs-base">{value || "-"}</p>
    </div>
);

const getPaymentStatusBg = (status) => {
    switch (status?.toLowerCase()) {
        case "paid":
        case "success":
            return "chip-green";
        case "failed":
            return "chip-red";
        case "pending":
        case "user_dropped":
            return "chip-warning";
        default:
            return "custom-status-chip";
    }
};

const parsePaymentMethodDetails = (details, group) => {
    if (!details) return "-";
    const method = details.payment_method || details;
    if (group === "upi" && method.upi) {
        return `UPI: ${method.upi.upi_id || "N/A"}`;
    }
    if (group === "card" && method.card) {
        const { card_bank_name, card_network, card_type, card_number } = method.card;
        const typeFormatted = card_type ? card_type.replace("_", " ") : "";
        return [card_bank_name, card_network, typeFormatted, card_number ? `ending in ${card_number}` : ""]
            .filter(Boolean)
            .join(" ")
            .toUpperCase();
    }
    return group ? group.toUpperCase() : "-";
};

const SubscriptionDetails = ({ subscriptionId, userDetail }) => {
    const dispatch = useDispatch();
    const toast = useToast();
    const [searchParams, setSearchParams] = useSearchParams();

    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [loadingId, setLoadingId] = useState(null);
    const [showReversePopup, setShowReversePopup] = useState(false);

    const { userSubscriptions } = useSelector(state => state.allUsers);
    const {
        paymentTransactions = [],
        totalPaymentTransactions = 0,
        isLoading
    } = useSelector(state => state.subscription || {});

    const subscription = useMemo(() => {
        if (!userSubscriptions || !subscriptionId) return null;
        return userSubscriptions.find(sub => String(sub.id) === String(subscriptionId));
    }, [userSubscriptions, subscriptionId]);

    const showQuickActions = subscription?.source === 'admin' && subscription?.status === 'active';
    const colClass = showQuickActions ? 'g-col-3' : 'g-col-4';

    const handleReverseConfirm = () => {
        if (!subscription) return;
        dispatch(reverseUserSubscription({ subscription_id: subscription.id }))
            .unwrap()
            .then((res) => {
                toast.success(res?.message || "Subscription revoked successfully");
                setShowReversePopup(false);
                if (userDetail?.id) {
                    dispatch(fetchUserDetail(userDetail.id));
                    dispatch(fetchUserSubscriptions({ id: userDetail.id, page: 1, limit: 10 }));
                }
            })
            .catch((err) => {
                toast.error(err?.message || "Failed to revoke subscription");
            });
    };

    useEffect(() => {
        if (subscriptionId) {
            dispatch(fetchPaymentTransactions({ page, limit, subscription_id: subscriptionId }));
        }
    }, [dispatch, subscriptionId, page, limit]);

    const handleDownloadInvoice = (item) => {
        setLoadingId(item.id);
        dispatch(downloadPaymentInvoice({ payment_transaction_id: item.id }))
            .unwrap()
            .then((data) => {
                if (data?.base64) {
                    const byteCharacters = atob(data.base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: "application/pdf" });
                    
                    saveAs(blob, data.filename || `invoice-${item.invoice_no ? item.invoice_no.replace(/\//g, "-") : item.id}.pdf`);
                } else {
                    toast.error("Invoice data not available.");
                }
            })
            .catch((err) => {
                toast.error(err?.message || "Failed to download invoice.");
            })
            .finally(() => {
                setLoadingId(null);
            });
    };

    const columns = [
        {
            header: "Invoice No",
            cell: (_, item) =>
                item.invoice_no ? (
                    <div className="text-nowrap" style={{ fontSize: "13px", fontFamily: "monospace" }}>
                        {item.invoice_no}
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "Start Date",
            cell: (_, item) =>
                item.start_date ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.start_date, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.start_date, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "End Date",
            cell: (_, item) =>
                item.end_date ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.end_date, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.end_date, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "Currency",
            cell: (_, item) => (
                <div className="text-nowrap fw-medium">{item.currency || "-"}</div>
            ),
        },
        {
            header: "Base Amount",
            cell: (_, item) => {
                const amount = item.base_amount;
                return amount != null ? (
                    <div className="text-nowrap">
                        {item.currency || "INR"} {parseFloat(amount).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "GST %",
            cell: (_, item) => {
                const gst = item.gst_percentage;
                return gst != null ? (
                    <div className="text-nowrap">{parseFloat(gst)}%</div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Tax Amount",
            cell: (_, item) => {
                const tax = item.tax_amount;
                return tax != null ? (
                    <div className="text-nowrap">
                        {item.currency || "INR"} {parseFloat(tax).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Payable Amount",
            cell: (_, item) => {
                const amount = item.payable_amount;
                return amount != null ? (
                    <div className="text-nowrap fw-semibold">
                        {item.currency || "INR"} {parseFloat(amount).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Status",
            cell: (_, item) =>
                item.status ? (
                    <StatusChip
                        bg={getPaymentStatusBg(item.status)}
                        pill
                        title={item.status}
                        className="text-capitalize"
                    />
                ) : (
                    "-"
                ),
        },
        {
            header: "Payment ID",
            cell: (_, item) => {
                const id = item.payment_id;
                return id ? (
                    <div className="text-nowrap" style={{ fontSize: "13px", fontFamily: "monospace" }}>
                        {id}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Payment Method",
            cell: (_, item) => {
                const group = item.payment_group || item.payment_method?.type;
                const details = item.payment_details || item.payment_method;
                return (
                    <div className="d-flex flex-column gap-1 text-nowrap">
                        <span className="text-capitalize fw-medium">{group || "-"}</span>
                        {details && (
                            <small className="text-muted">
                                {parsePaymentMethodDetails(details, group)}
                            </small>
                        )}
                    </div>
                );
            },
        },
        {
            header: "Service Charge",
            cell: (_, item) => {
                const val = item.service_charge;
                return val != null ? (
                    <div className="text-nowrap">
                        {item.currency || "INR"} {parseFloat(val).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Service Tax",
            cell: (_, item) => {
                const val = item.service_tax;
                return val != null ? (
                    <div className="text-nowrap">
                        {item.currency || "INR"} {parseFloat(val).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Settlement Amount",
            cell: (_, item) => {
                const val = item.settlement_amount;
                return val != null ? (
                    <div className="text-nowrap fw-medium">
                        {item.currency || "INR"} {parseFloat(val).toLocaleString("en-IN")}
                    </div>
                ) : (
                    "-"
                );
            },
        },
        {
            header: "Error Details",
            cell: (_, item) => {
                const err = item.error_details;
                if (!err) return "-";

                // Cashfree error objects typically have: error_code, error_description, error_reason
                const code = err.error_code || err.code || null;
                const desc = err.error_description || err.description || null;
                const reason = err.error_reason || err.reason || null;
                const fullText = typeof err === "string" ? err : JSON.stringify(err, null, 2);

                if (typeof err === "string") {
                    return (
                        <div
                            className="small text-danger"
                            style={{ maxWidth: "220px", wordBreak: "break-word", cursor: "help" }}
                            title={err}
                        >
                            {err}
                        </div>
                    );
                }

                return (
                    <div
                        className="d-flex flex-column gap-1 small text-danger"
                        style={{ maxWidth: "220px", cursor: "help" }}
                        title={fullText}
                    >
                        {code && (
                            <span className="fw-semibold text-nowrap">{code}</span>
                        )}
                        {desc && (
                            <span style={{ wordBreak: "break-word", whiteSpace: "normal" }}>{desc}</span>
                        )}
                        {reason && (
                            <span className="text-muted text-nowrap">Reason: {reason}</span>
                        )}
                        {!code && !desc && !reason && (
                            <span
                                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                                {fullText}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            header: "Paid At",
            cell: (_, item) =>
                item.paid_at ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.paid_at, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.paid_at, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "Created At",
            cell: (_, item) =>
                item.created_at ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.created_at, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.created_at, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
        {
            header: "Updated At",
            cell: (_, item) =>
                item.updated_at ? (
                    <div className="d-flex flex-column text-nowrap">
                        <span>{formatMomentDate(item.updated_at, "DD MMM YYYY")}</span>
                        <small className="text-muted">{formatMomentDate(item.updated_at, "hh:mm A")}</small>
                    </div>
                ) : (
                    "-"
                ),
        },
    ];

    const actionList = [
        {
            title: "Download Invoice",
            isVisible: (row) => row.status === 'paid',
            isLoading: (row) => loadingId === row.id,
            handler: (row) => handleDownloadInvoice(row)
        }
    ];

    const handleBack = () => {
        const updated = new URLSearchParams(searchParams);
        updated.delete('subscription-id');
        setSearchParams(updated);
    };

    const getCreatedBy = () => {
        if (!subscription) return "-";
        if (subscription.created_by_type === 'admin' && subscription.creator_admin) {
            return `Admin: ${subscription.creator_admin.name}`;
        }
        if (subscription.created_by_type === 'user' && subscription.creator_user) {
            return `User: ${subscription.creator_user.first_name} ${subscription.creator_user.last_name}`;
        }
        return "-";
    };

    if (!subscription) {
        return (
            <div className="position-relative h-100">
                <div className="d-flex align-items-center gap-2 mb-4">
                    <button
                        onClick={handleBack}
                        type="button"
                        className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center"
                        style={{ cursor: 'pointer' }}
                    >
                        <ArrowLeft size={20} strokeWidth={2.2} />
                    </button>
                    <h6 className="fw-bold mb-0" style={{ fontSize: '18px' }}>Subscription Details</h6>
                </div>
                <div className="border rounded-3 bg-white p-4 text-center">
                    <p className="text-muted mb-0">Subscription not found.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="position-relative h-100">
            {/* Header */}
            <div className="d-flex align-items-center gap-2 mb-4">
                <button
                    onClick={handleBack}
                    type="button"
                    className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center"
                    style={{ cursor: 'pointer' }}
                >
                    <ArrowLeft size={20} strokeWidth={2.2} />
                </button>
                <h6 className="fw-bold mb-0" style={{ fontSize: '18px' }}>Subscription Details</h6>
            </div>

            {/* Main Card */}
            <div className="border rounded-3 overflow-hidden bg-white">
                <div className="grid gap-0">

                    {/* Plan Info Section */}
                    <div className={`p-4 d-flex align-items-start gap-3 border-end ${colClass}`} style={{ minWidth: '220px' }}>
                        <div
                            className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                            style={{
                                width: '64px',
                                height: '64px',
                                background: 'linear-gradient(135deg, #fff0f0, #ffe0e0)',
                            }}
                        >
                            <Crown size={28} style={{ color: 'var(--primary, #e74c6f)' }} />
                        </div>
                        <div className="d-flex flex-column gap-1">
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="fw-bold">
                                    {subscription.plan?.name || "-"}
                                </span>
                                {subscription.status && (
                                    <StatusChip
                                        bg={getStatusBg(subscription.status)}
                                        pill
                                        title={subscription.status}
                                        className="text-capitalize"
                                    />
                                )}
                            </div>
                            <span className="text-muted text-capitalize fs-sm">
                                {subscription.plan?.interval || "-"} Plan
                            </span>

                            <div className="d-flex align-items-start gap-2 mt-3">
                                <Globe size={12} className="text-muted lh-1" />
                                <div>
                                    <p className="text-muted fs-sm lh-1 mb-1">Source</p>
                                    <span className="fw-medium text-capitalize fs-md">
                                        {subscription.source || "-"}
                                    </span>
                                </div>
                            </div>


                            <div className="d-flex align-items-start gap-2 mt-2">
                                <RefreshCw size={12} className="text-muted lh-1" />
                                <div>
                                    <p className="text-muted fs-sm lh-1 mb-1">Auto Renewal</p>
                                    {subscription.auto_renewal ? (
                                        <>
                                            <CheckCircle2 size={14} className='text-success me-2 lh-1' />
                                            <span className="fw-medium fs-md lh-1">Enabled</span>
                                        </>
                                    ) : (
                                        <>
                                            <XCircle size={14} className="text-muted me-2 lh-1" />
                                            <span className="fw-medium text-muted fs-md lh-1">Disabled</span>
                                        </>
                                    )}
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Subscription Details Section */}
                    <div className={`p-4 border-end ${colClass}`} style={{ minWidth: '260px' }}>

                        <DetailRow icon={Hash} label="Subscription ID" value={subscription.id} />
                        <DetailRow icon={Hash} label="External Subscription ID" value={subscription.external_subscription_id || subscription.external_id} />
                        <DetailRow icon={CreditCard} label="Product ID" value={subscription.product_id || subscription.plan?.apple_product_id} />
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-muted small" style={{ fontSize: '11px', lineHeight: '1.2' }}>Status</span>
                            {subscription.status ? (
                                <StatusChip
                                    bg={getStatusBg(subscription.status)}
                                    pill
                                    title={subscription.status}
                                    className="text-capitalize mt-1"
                                />
                            ) : (
                                <span className="fw-medium" style={{ fontSize: '13px' }}>-</span>
                            )}

                        </div>
                    </div>

                    {/* Dates & Info Section */}
                    <div className={`p-4 ${colClass} ${showQuickActions ? 'border-end' : ''}`} style={{ minWidth: '240px' }}>
                        <div className="grid gap-2">
                            <span className='g-col-4 fs-sm text-secondary'>Start Date</span>
                            <p className="g-col-8 fs-md text-primary">{subscription.start_date ? formatMomentDate(subscription.start_date, "DD MMM YYYY, hh:mm A") : "-"}</p>

                            <span className='g-col-4 fs-sm text-secondary '>Trial</span>
                            <p className="g-col-8"><StatusChip bg={subscription.is_trial ? "chip-primary" : "chip-secondary"} pill title={subscription.is_trial ? "Yes" : "No"} /></p>
                            <span className='g-col-4 fs-sm text-secondary'>End Date</span>
                            <p className="g-col-8 fs-md text-primary">{subscription.end_date ? formatMomentDate(subscription.end_date, "DD MMM YYYY, hh:mm A") : "-"}</p>

                            <span className='g-col-4 fs-sm text-secondary'>Next Renewal On</span>
                            <p className="g-col-8 fs-md text-primary">{subscription.next_renewal_date ? formatMomentDate(subscription.next_renewal_date, "DD MMM YYYY, hh:mm A") : "-"}</p>

                            <span className='g-col-4 fs-sm text-secondary'>Created By</span>
                            <p className="g-col-8 fs-md text-primary">{getCreatedBy()}</p>
                            <span className='g-col-4 fs-sm text-secondary'>Created At</span>
                            <p className="g-col-8 fs-md text-primary">{subscription.created_at ? formatMomentDate(subscription.created_at, "DD MMM YYYY, hh:mm A") : "-"}</p>


                        </div>
                    </div>

                    {/* Quick Actions Section */}
                    {showQuickActions && (
                        <div className="p-4 d-flex flex-column g-col-3" style={{ minWidth: '200px' }}>
                            <h6 className="fw-bold mb-3 f-md" >Quick Actions</h6>
                            <div className="d-flex flex-column gap-2">
                                <CommonButton
                                    icon={<Ban size={15} />}
                                    title="Revoke Plan"
                                    variant="outline-danger"
                                    onClick={() => setShowReversePopup(true)}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {/* Payment Summary Cards */}
            <div className="grid gap-3 mt-4">
                <TabCard
                    className="g-col-4"
                    icon={<Receipt size={20} />}
                    iconClassName="icon-info"
                    title="Total Payments"
                    count={subscription.total_payment_transactions != null ? subscription.total_payment_transactions : (subscription.payment_transactions?.length || subscription.total_payments || 0)}
                />
                <TabCard
                    className="g-col-4"
                    icon={<IndianRupee size={20} />}
                    iconClassName="icon-warning"
                    title="Total Amount Paid"
                    count={`₹ ${subscription.total_amount_paid != null ? parseFloat(subscription.total_amount_paid).toLocaleString() : (subscription.payment_transaction?.payable_amount ? parseFloat(subscription.payment_transaction.payable_amount).toLocaleString() : '0')}`}
                />
                <TabCard
                    className="g-col-4"
                    icon={<CalendarCheck size={20} />}
                    iconClassName="icon-success"
                    title="Last Payment"
                    count={subscription.last_payment_date ? formatMomentDate(subscription.last_payment_date, 'DD MMM YYYY') : (subscription.payment_transaction?.paid_at ? formatMomentDate(subscription.payment_transaction.paid_at, 'DD MMM YYYY') : '-')}
                />  
            </div>
            <Card className='mt-4 bg-white'>
                <Card.Header className='bg-transparent primary'>Renewal History</Card.Header>
                <CustomTable
                    columns={columns}
                    data={paymentTransactions}
                    loading={isLoading}
                    actionList={actionList}
                    emptyMessage="No payment transactions found for this subscription."
                    pagination={{
                        currentPage: page,
                        totalPages: Math.ceil((totalPaymentTransactions || 0) / limit) || 1,
                        total: totalPaymentTransactions,
                        onPageChange: (newPage) => setPage(newPage),
                        pageSize: limit,
                        setPageSize: (newLimit) => {
                            setLimit(newLimit);
                            setPage(1);
                        },
                        setCurrentPage: (newPage) => setPage(newPage)
                    }}
                />
            </Card>

            <ConfirmModal
                isOpen={showReversePopup}
                onClose={() => setShowReversePopup(false)}
                title="Revoke Plan"
                message="Are you sure you want to revoke this plan?"
                onConfirm={handleReverseConfirm}
            />
        </div>
    );
};

export default SubscriptionDetails;